import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { ArrayWithAgents } from '../models/array-with-agents';
import { GenericService } from './generic.service';
import { SortParams } from '../interfaces/list-params';
import { forkJoin, of, Observable, take, shareReplay } from 'rxjs';
import { tap, map, catchError, switchMap } from 'rxjs/operators';
import { FeatureFlagDxpService } from '@pure1/data';
import { FeatureNames } from '../../model/model';

const PERF_AGENT_NAME = 'e-one-perf-mgmt-agent';
const BACKEND_CALL_BATCH_SIZE = 50;
interface CapacityAgentArray {
    cloud_array_id?: string;
    id?: string;
    capacity_expandable?: boolean;
    capacityExpandable?: boolean;
    capacityAgentInstalled?: boolean;
    capacityAgentRunningVersion?: string;
    capacityAgentRecommendedVersion?: string;
}

@Injectable({ providedIn: 'root' })
export class EdgeServiceArraysService extends GenericService<ArrayWithAgents> {
    cdlExpansionAutomationFF$: Observable<boolean>;

    constructor(
        protected http: HttpClient,
        protected featureFlagDxpService: FeatureFlagDxpService,
    ) {
        super({
            resourceClass: ArrayWithAgents,
            endpoint: '/rest/v1/ems/arrays',
            defaultParams: {
                sort: <SortParams>{
                    key: 'hostname',
                    order: 'asc',
                },
            },
            // create, update, delete are assumed to be non-supported
        });
    }

    /**
     * Populates each array in the provided list with Entitlement Agent information.
     *
     * This method queries the EMS endpoint once with a list of array IDs and updates each array in the input list with:
     *  - capacityAgentInstalled: true if the agent is installed, false otherwise
     *  - capacityAgentRunningVersion: the currently running agent version (if installed)
     *  - capacityAgentRecommendedVersion: the recommended agent version (if installed)
     *
     * This is used to determine which arrays need agent installation or an update.
     *
     * @param originalArrays The list of arrays to populate with agent information. Each array must have a cloud_array_id or id.
     * @param cdlArraysOnly If true, only arrays that are capacity expandable will be processed; otherwise, all arrays are processed.
     * @returns An Observable emitting the updated list of arrays with agent information populated.
     */
    populateCapacityAgentInfo<T extends CapacityAgentArray>(
        originalArrays: T[],
        cdlArraysOnly: boolean,
    ): Observable<T[]> {
        if (!originalArrays || originalArrays.length === 0) {
            return of(originalArrays);
        }

        return this.featureFlagDxpService.getFeatureFlag(FeatureNames.CDL_EXPANSION_AUTOMATION).pipe(
            map(feature => feature?.enabled === true),
            catchError(() => {
                console.error('Failed to fetch CDL Expansion Automation feature flag');
                // Return false if the feature flag cannot be fetched
                return of(false);
            }),
            switchMap(cdlExpansionAutomationFF => {
                if (!cdlExpansionAutomationFF) {
                    return of(originalArrays);
                }

                const filteredArrays = cdlArraysOnly
                    ? originalArrays.filter(array => array.capacity_expandable || array.capacityExpandable)
                    : originalArrays;

                const arrayMap = new Map(filteredArrays.map(array => [array.cloud_array_id || array.id, array]));
                const arrayIds = Array.from(arrayMap.keys());
                if (arrayIds.length === 0) {
                    return of(originalArrays);
                }

                const batches: string[][] = [];
                for (let i = 0; i < arrayIds.length; i += BACKEND_CALL_BATCH_SIZE) {
                    batches.push(arrayIds.slice(i, i + BACKEND_CALL_BATCH_SIZE));
                }

                const batchRequests = batches.map(batchIds => {
                    const commaSeparatedArrayIds = batchIds.map(id => `'${id}'`).join(',');
                    const requestParams = {
                        filter: {
                            array_id: `contains(array_id, (${commaSeparatedArrayIds}))`,
                        },
                    };
                    return this.list(requestParams).pipe(
                        tap(emsResponse => {
                            const arrayAgentsList: ArrayWithAgents[] = emsResponse.response;
                            arrayAgentsList.forEach(arrayAgent => {
                                const perfAgent = arrayAgent.agents.find(agent => agent.name === PERF_AGENT_NAME);
                                const array = arrayMap.get(arrayAgent.id);
                                if (array) {
                                    if (perfAgent) {
                                        array.capacityAgentInstalled = true;
                                        array.capacityAgentRunningVersion = perfAgent.running_version;
                                        array.capacityAgentRecommendedVersion = perfAgent.available_version;
                                    } else {
                                        array.capacityAgentInstalled = false;
                                    }
                                } else {
                                    console.error(`Array with ID ${arrayAgent.id} not found in input list`);
                                }
                            });
                        }),
                    );
                });
                return forkJoin(batchRequests).pipe(map(() => originalArrays));
            }),
            catchError(error => {
                console.error('Failed to populate capacity agent info:', error);
                return of(originalArrays);
            }),
        );
    }
}

@Injectable({ providedIn: 'root' })
export class EdgeServiceArrayProgressService extends GenericService<ArrayWithAgents> {
    constructor(protected http: HttpClient) {
        super({
            resourceClass: ArrayWithAgents,
            endpoint: '/rest/v1/ems/arrays/agent-in-progress',
            defaultParams: {
                sort: <SortParams>{
                    key: 'hostname',
                    order: 'asc',
                },
            },
            // create, update, delete are assumed to be non-supported
        });
    }
}
